import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  FolderTree,
  FileText,
  Building2,
} from "lucide-react";
import { clsx } from "clsx";
import { api } from "@/api/client";
import {
  getDocumentMatrix,
  getDocumentMatrixCell,
  type DocumentMatrixResponse,
  type MatrixFilters,
} from "@/api/documentMatrix";
import type { DocumentListResponse } from "@/types/document";

interface MasterRef {
  id: string;
  kode?: string;
  nama: string;
}

export default function DocumentMatrixPage() {
  const [filters, setFilters] = useState<MatrixFilters>({});
  const [stakeholderFilter, setStakeholderFilter] = useState<string>("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<{
    stakeholderId: string;
    stakeholderNama: string;
    dimensiId: string;
    dimensiNama: string;
  } | null>(null);

  const { data: matrix, isLoading } = useQuery<DocumentMatrixResponse>({
    queryKey: ["document-matrix", filters],
    queryFn: () => getDocumentMatrix(filters),
  });

  const { data: jenisList = [] } = useQuery<MasterRef[]>({
    queryKey: ["master", "jenis-pnbp"],
    queryFn: () => api.get("/master/jenis-pnbp"),
  });

  const { data: cellDocs, isLoading: cellLoading } = useQuery<DocumentListResponse>({
    queryKey: ["matrix-cell", selected?.stakeholderId, selected?.dimensiId, filters],
    queryFn: () =>
      getDocumentMatrixCell({
        stakeholder_id: selected!.stakeholderId,
        dimensi_id: selected!.dimensiId,
        page: 1,
        page_size: 100,
        ...filters,
      }),
    enabled: !!selected,
  });

  // Build cell lookup: "stakeholderId|dimensiId" → count
  const cellMap = useMemo(() => {
    const m = new Map<string, number>();
    matrix?.cells.forEach((c) =>
      m.set(`${c.stakeholder_id}|${c.dimensi_id}`, c.count),
    );
    return m;
  }, [matrix]);

  const toggleExpand = (stakeholderId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(stakeholderId)) next.delete(stakeholderId);
      else next.add(stakeholderId);
      return next;
    });
  };

  const tahunOptions = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => now - i);
  }, []);

  const updateFilter = <K extends keyof MatrixFilters>(
    key: K,
    value: MatrixFilters[K] | "",
  ) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === "" || value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  };

  const filteredStakeholders = useMemo(() => {
    if (!matrix) return [];
    if (!stakeholderFilter) return matrix.stakeholders;
    return matrix.stakeholders.filter((s) => s.id === stakeholderFilter);
  }, [matrix, stakeholderFilter]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <FolderTree className="h-5 w-5 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Pohon Pengetahuan</h1>
        </div>
        <p className="mt-0.5 text-sm text-gray-500">
          Telusuri dokumen berdasarkan Stakeholder → Dimensi
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">
            Stakeholder
          </label>
          <select
            value={stakeholderFilter}
            onChange={(e) => setStakeholderFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Semua</option>
            {(matrix?.stakeholders ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.nama}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">
            Jenis PNBP
          </label>
          <select
            value={filters.jenis_pnbp_id ?? ""}
            onChange={(e) => updateFilter("jenis_pnbp_id", e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Semua</option>
            {jenisList.map((j) => (
              <option key={j.id} value={j.id}>
                {j.kode} — {j.nama}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">
            Tahun
          </label>
          <select
            value={filters.tahun ?? ""}
            onChange={(e) =>
              updateFilter("tahun", e.target.value ? Number(e.target.value) : "")
            }
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">Semua</option>
            {tahunOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        {(stakeholderFilter || filters.jenis_pnbp_id || filters.tahun) && (
          <button
            type="button"
            onClick={() => { setFilters({}); setStakeholderFilter(""); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
          >
            Reset
          </button>
        )}

        <div className="ml-auto text-sm text-gray-500">
          Total dokumen:{" "}
          <span className="font-bold text-gray-900">
            {matrix?.totals.grand ?? 0}
          </span>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[360px_minmax(0,1fr)]">
        {/* ─── LEFT: Tree ─────────────────────────────────────────────────── */}
        <div className="rounded-lg border border-gray-200 bg-white flex flex-col">
          <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <h2 className="text-sm font-semibold text-gray-700">Stakeholder</h2>
            {filteredStakeholders.length !== (matrix?.stakeholders.length ?? 0) && (
              <span className="text-xs text-blue-600 font-medium">
                {filteredStakeholders.length} dari {matrix?.stakeholders.length}
              </span>
            )}
          </div>
          {isLoading ? (
            <p className="px-4 py-6 text-sm text-gray-500">Memuat…</p>
          ) : !matrix ? null : (
            <ul className="divide-y divide-gray-100 overflow-y-auto max-h-[520px]">
              {filteredStakeholders.map((s) => {
                const isOpen = expanded.has(s.id);
                const total = matrix.totals.by_stakeholder[s.id] ?? 0;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => toggleExpand(s.id)}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-50"
                    >
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                      <Building2 className="h-4 w-4 text-blue-600" />
                      <span className="flex-1 truncate text-sm font-medium text-gray-800">
                        {s.nama}
                      </span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                        {total}
                      </span>
                    </button>
                    {isOpen && (
                      <ul className="bg-gray-50/50 pb-2">
                        {matrix.dimensi.map((d) => {
                          const count = cellMap.get(`${s.id}|${d.id}`) ?? 0;
                          const isSelected =
                            selected?.stakeholderId === s.id &&
                            selected?.dimensiId === d.id;
                          return (
                            <li key={d.id}>
                              <button
                                type="button"
                                disabled={count === 0}
                                onClick={() =>
                                  setSelected({
                                    stakeholderId: s.id,
                                    stakeholderNama: s.nama,
                                    dimensiId: d.id,
                                    dimensiNama: d.nama,
                                  })
                                }
                                className={clsx(
                                  "flex w-full items-center gap-2 py-1.5 pl-12 pr-4 text-left text-sm",
                                  count === 0
                                    ? "cursor-default text-gray-300"
                                    : "text-gray-700 hover:bg-blue-50",
                                  isSelected && "bg-blue-100 font-semibold text-blue-800",
                                )}
                              >
                                <span className="flex-1">{d.nama}</span>
                                <span
                                  className={clsx(
                                    "rounded-full px-1.5 py-0.5 text-xs font-semibold",
                                    count === 0
                                      ? "bg-gray-100 text-gray-300"
                                      : "bg-white text-gray-600",
                                  )}
                                >
                                  {count}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* ─── RIGHT: Document list ───────────────────────────────────────── */}
        <div className="min-w-0 rounded-lg border border-gray-200 bg-white overflow-hidden">
          {!selected ? (
            <div className="flex h-full min-h-[400px] flex-col items-center justify-center text-center text-sm text-gray-400">
              <FolderTree className="mb-3 h-10 w-10 text-gray-300" />
              <p>Pilih stakeholder, lalu pilih dimensi untuk melihat dokumen.</p>
            </div>
          ) : (
            <>
              <div className="border-b border-gray-200 px-5 py-4">
                <p className="text-xs uppercase tracking-wide text-gray-400">
                  {selected.stakeholderNama}
                </p>
                <h2 className="text-lg font-bold text-gray-900">
                  {selected.dimensiNama}
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">
                  {cellDocs?.total ?? 0} dokumen
                </p>
              </div>
              <div className="px-5 py-4 overflow-y-auto max-h-[calc(100vh-20rem)]">
                {cellLoading ? (
                  <p className="text-sm text-gray-500">Memuat…</p>
                ) : !cellDocs || cellDocs.items.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    Tidak ada dokumen pada dimensi ini.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {cellDocs.items.map((doc) => (
                      <li key={doc.id}>
                        <Link
                          to={`/documents/${doc.id}`}
                          className="flex items-start gap-3 rounded-lg border border-gray-200 px-3 py-3 hover:border-blue-300 hover:bg-blue-50"
                        >
                          <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium text-gray-900">
                              {doc.judul}
                            </p>
                            {doc.nomor_dokumen && (
                              <p className="truncate text-xs text-gray-500">
                                {doc.nomor_dokumen}
                              </p>
                            )}
                            <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                                {doc.jenis_pnbp_kode}
                              </span>
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                                {doc.status_nama}
                              </span>
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
